import { Check, Sparkles } from "lucide-react";
import { LOGO_URL, formatWeddingDate } from "@/lib/quoteContent";

export function AcceptedQuoteView({ quote }) {
  const selId = quote.acceptance?.selected_package_id || quote.selected_package_id;
  const pkg = (quote.packages || []).find((p) => p.id === selId);
  const weddingDate = formatWeddingDate(quote.wedding_date);

  return (
    <div
      data-testid="accepted-quote-view"
      className="min-h-screen bg-[#21201F] text-white px-6 py-16 md:py-24 flex flex-col items-center"
    >
      <img
        src={LOGO_URL}
        alt="Wedding Video Essex"
        className="h-20 md:h-24 w-auto object-contain brightness-0 invert opacity-95 mb-10"
      />
      <Sparkles className="w-7 h-7 text-[#CCBA56] mb-4" strokeWidth={1.25} />
      <div className="text-xs tracking-overline uppercase text-[#CCBA56] mb-4 font-medium">
        Your Date Is Secured
      </div>
      <h1 className="font-display text-4xl md:text-6xl tracking-tight leading-tight mb-6 text-center max-w-3xl">
        Welcome to the family,
        <br />
        <span className="italic">{quote.partner1_name} &amp; {quote.partner2_name}!</span>
      </h1>
      <p className="text-white/70 leading-relaxed mb-4 max-w-xl text-center">
        Thank you so much for having us as part of your wedding day. It
        genuinely means the world to us, and we can't wait to capture every
        moment
        {quote.venue_name && (
          <> at <span className="text-white italic">{quote.venue_name}</span></>
        )}
        {weddingDate && (
          <> on <span className="text-white italic">{weddingDate}</span></>
        )}
        .
      </p>
      <p className="text-white/70 leading-relaxed mb-12 max-w-xl text-center">
        Your booking is officially locked in and your date is in our diary.
        Here's what you've secured:
      </p>

      {pkg && (
        <div
          data-testid="accepted-package-card"
          className="bg-white/5 border border-[#CCBA56]/40 max-w-lg w-full p-8 md:p-10 mb-12"
        >
          <div className="text-[10px] tracking-overline uppercase text-[#CCBA56] font-medium mb-3">
            Your Collection
          </div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-2">
            {pkg.name}
          </h2>
          <div className="font-display text-2xl text-[#CCBA56] mb-8">
            £{Number(pkg.price).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
          </div>
          <ul className="space-y-3 text-left">
            {(pkg.features || []).map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-white/80 leading-relaxed">
                <Check className="w-4 h-4 text-[#CCBA56] mt-0.5 shrink-0" strokeWidth={2.5} />
                {f}
              </li>
            ))}
            {(quote.acceptance?.addons || []).map((a) => (
              <li key={a.key} className="flex items-start gap-3 text-sm leading-relaxed">
                <Check className="w-4 h-4 text-[#CCBA56] mt-0.5 shrink-0" strokeWidth={2.5} />
                <span className="text-[#CCBA56] font-medium">
                  {a.name} (+£{Number(a.price).toLocaleString("en-GB")})
                </span>
              </li>
            ))}
            {(quote.acceptance?.prewedding_shoot_included) && (
              <li data-testid="accepted-prewedding" className="flex items-start gap-3 text-sm leading-relaxed">
                <Check className="w-4 h-4 text-[#CCBA56] mt-0.5 shrink-0" strokeWidth={2.5} />
                <span className="text-[#CCBA56] font-medium">
                  Pre-Wedding Couple Photoshoot (included free)
                </span>
              </li>
            )}
            {(quote.acceptance?.video_guestbook_included) && (
              <li data-testid="accepted-guestbook" className="flex items-start gap-3 text-sm leading-relaxed">
                <Check className="w-4 h-4 text-[#CCBA56] mt-0.5 shrink-0" strokeWidth={2.5} />
                <span className="text-[#CCBA56] font-medium">
                  Video Messages Guestbook (included free)
                </span>
              </li>
            )}
          </ul>
        </div>
      )}

      <p className="text-white/50 text-sm max-w-md mb-8 text-center leading-relaxed">
        Keep an eye on your inbox for your welcome email and the install
        instructions for our booking &amp; planning app.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <a
          href="https://wa.link/aorw15"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="accepted-whatsapp-btn"
          className="inline-flex items-center gap-2 bg-[#CCBA56] hover:bg-[#B5A244] text-[#1F1E1D] px-8 py-4 text-xs tracking-overline uppercase font-medium transition-colors"
        >
          Say hi on WhatsApp
        </a>
        <a
          href="https://www.weddingvideo.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="accepted-website-btn"
          className="inline-flex items-center gap-2 border border-white/40 hover:border-white text-white px-8 py-4 text-xs tracking-overline uppercase font-medium transition-colors"
        >
          Visit weddingvideo.co.uk
        </a>
      </div>
    </div>
  );
}
